
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import SettingsTabsHeader from "@/components/settings/SettingsTabsHeader";
import { ProfileSettings } from "./ProfileSettings";
import { AccountSettings } from "./AccountSettings";
import { BillingSettings } from "./BillingSettings";
import { PageContainer } from "@/components/ui/PageContainer";

type SettingsTab = "profile" | "account" | "billing";

const settingsTabs = [
  { id: "profile" as const, label: "Profile" },
  { id: "account" as const, label: "Account" }, 
  { id: "billing" as const, label: "Billing" },
];

export function SettingsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine active tab from URL
  const getActiveTab = (): SettingsTab => {
    if (location.pathname === "/settings/account") return "account";
    if (location.pathname === "/settings/billing") return "billing";
    return "profile";
  };

  const [activeTab, setActiveTab] = useState<SettingsTab>(getActiveTab());

  const handleTabChange = (tabId: SettingsTab) => {
    setActiveTab(tabId);
    switch (tabId) {
      case "profile":
        navigate("/settings");
        break;
      case "account":
        navigate("/settings/account");
        break;
      case "billing":
        navigate("/settings/billing");
        break;
    }
  };

  const getSubtitle = () => {
    switch (activeTab) {
      case "profile":
        return "Manage your personal information and preferences";
      case "account":
        return "Configure your account security and authentication settings";
      case "billing":
        return "View and manage your subscription and billing information";
      default:
        return "Manage your personal information and preferences";
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "account":
        return <AccountSettings />;
      case "billing":
        return <BillingSettings />;
      default:
        return <ProfileSettings />;
    }
  };

  return (
    <SidebarInset>
      <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4 sticky top-0 bg-background z-10">
        <SidebarTrigger className="-ml-2" />
        <Separator orientation="vertical" className="h-6" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <Link to="/">Dashboard</Link>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Settings</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </header>
      <PageContainer maxWidthClassName="max-w-[960px]">
        <div className="flex flex-col">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Settings</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {getSubtitle()}
              </p>
            </div>
          </div>
          <div className="mt-6">
            <SettingsTabsHeader 
              tabs={settingsTabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
          </div>
          <div className="mt-4">
            {renderTabContent()}
          </div>
        </div>
      </PageContainer>
    </SidebarInset>
  );
}
